import { useState, useRef, useEffect } from 'react';
import { Play, X, ChevronLeft, ChevronRight, Building2 } from 'lucide-react';

const ClientSuccessStories = () => {
  const [activeVideo, setActiveVideo] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const videoRef = useRef(null);
  const sliderRef = useRef(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const testimonials = [
    {
      id: 1,
      caption: "Seamless office transformation delivered on time with outstanding quality.",
      client: "Ms. Sunny Leone",
      designation: "Head",
      company: "Sun City Media & Entertainment Pvt. Ltd.",
      videoUrl: "https://customer-assets.emergentagent.com/job_d65c8eca-678a-4335-83ba-fb87545b1efd/artifacts/w87egmxi_Final%201.mp4",
      thumbnailUrl: "/thumbnails/thumb1.jpg"
    },
    {
      id: 2,
      caption: "From bare shell to fully operational workspace — flawless execution.",
      client: "Mr. Mikal Raorai",
      designation: "VP & CFO",
      company: "Sky Industries Ltd.",
      videoUrl: "https://customer-assets.emergentagent.com/job_d65c8eca-678a-4335-83ba-fb87545b1efd/artifacts/99b906rc_Final%202.mov",
      thumbnailUrl: "/thumbnails/thumb2.jpg"
    },
    {
      id: 3,
      caption: "Highly efficient modular solutions that optimized our office productivity.",
      client: "Mr. Saurabh Phatak",
      designation: "Procurement Division",
      company: "Royal Pharmaceuticals Pvt. Ltd.",
      videoUrl: "https://customer-assets.emergentagent.com/job_d65c8eca-678a-4335-83ba-fb87545b1efd/artifacts/3xzakuff_Final%203.mp4",
      thumbnailUrl: "/thumbnails/thumb3.jpg"
    },
    {
      id: 4,
      caption: "Professional planning, premium finish, and exceptional service experience.",
      client: "Mr. Krishna Kumar",
      designation: "Purchase Head",
      company: "Odyssey Logistics Pvt. Ltd.",
      videoUrl: "https://customer-assets.emergentagent.com/job_d65c8eca-678a-4335-83ba-fb87545b1efd/artifacts/bmh3js28_Final%204.mp4",
      thumbnailUrl: "/thumbnails/thumb4.jpg"
    }
  ];

  // Auto-play next video in modal
  useEffect(() => {
    if (activeVideo && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [activeVideo]);

  // Handle modal close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setActiveVideo(null);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe && currentSlide < testimonials.length - 1) {
      setCurrentSlide(prev => prev + 1);
    }
    if (isRightSwipe && currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const openVideo = (testimonial) => {
    setActiveVideo(testimonial);
  };

  const closeModal = () => {
    setActiveVideo(null);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  return (
    <>
      <section className="py-20 bg-white" data-testid="client-success-stories">
        <div className="container mx-auto px-4 sm:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <p className="text-[#c5a059] text-sm tracking-[0.3em] uppercase mb-4 font-medium">
              Client Success Stories
            </p>
            <h2 
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-4"
              
            >
              Trusted by Businesses Across India
            </h2>
            <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto">
              See how we transformed workspaces with smart modular solutions
            </p>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:block relative max-w-6xl mx-auto">
            <div className="grid grid-cols-4 gap-6">
              {testimonials.map((testimonial, index) => (
                <div 
                  key={testimonial.id}
                  className="group cursor-pointer"
                  onClick={() => openVideo(testimonial)}
                  data-testid={`testimonial-card-${index}`}
                >
                  {/* Thumbnail Image */}
                  <div className="relative aspect-[9/16] overflow-hidden rounded-lg shadow-lg border border-gray-200 transition-all duration-300 group-hover:shadow-xl group-hover:border-[#c5a059]">
                    <img 
                      src={testimonial.thumbnailUrl}
                      alt={`${testimonial.client} - ${testimonial.company}`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/80 via-[#1a1a1a]/30 to-transparent" />
                    
                    {/* Play Button */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-14 h-14 bg-[#c5a059] rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:bg-white shadow-lg">
                        <Play className="w-6 h-6 text-[#1a1a1a] ml-1" fill="#1a1a1a" />
                      </div>
                    </div>
                  </div>

                  {/* Caption & Client Details */}
                  <div className="mt-4 text-center">
                    <p className="text-sm text-gray-700 leading-relaxed line-clamp-2 group-hover:text-[#c5a059] transition-colors mb-3">
                      "{testimonial.caption}"
                    </p>
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-[#1a1a1a]">{testimonial.client}</p>
                      <p className="text-xs text-gray-500">{testimonial.designation}</p>
                      <div className="flex items-center justify-center gap-1.5 mt-1">
                        <Building2 className="w-3.5 h-3.5 text-[#c5a059]" />
                        <span className="text-xs text-gray-600 font-medium">{testimonial.company}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Carousel */}
          <div 
            className="md:hidden relative max-w-sm mx-auto"
            ref={sliderRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-300 ease-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {testimonials.map((testimonial, index) => (
                  <div 
                    key={testimonial.id}
                    className="w-full flex-shrink-0 px-2"
                    onClick={() => openVideo(testimonial)}
                    data-testid={`mobile-testimonial-card-${index}`}
                  >
                    {/* Thumbnail Image */}
                    <div className="relative aspect-[9/16] overflow-hidden rounded-lg shadow-lg border border-gray-200">
                      <img 
                        src={testimonial.thumbnailUrl}
                        alt={`${testimonial.client} - ${testimonial.company}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/80 via-[#1a1a1a]/30 to-transparent" />
                      
                      {/* Play Button */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-14 h-14 bg-[#c5a059] rounded-full flex items-center justify-center shadow-lg">
                          <Play className="w-6 h-6 text-[#1a1a1a] ml-1" fill="#1a1a1a" />
                        </div>
                      </div>
                    </div>

                    {/* Caption & Client Details */}
                    <div className="mt-4 text-center">
                      <p className="text-sm text-gray-700 leading-relaxed mb-3">
                        "{testimonial.caption}"
                      </p>
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-[#1a1a1a]">{testimonial.client}</p>
                        <p className="text-xs text-gray-500">{testimonial.designation}</p>
                        <div className="flex items-center justify-center gap-1.5 mt-1">
                          <Building2 className="w-3.5 h-3.5 text-[#c5a059]" />
                          <span className="text-xs text-gray-600 font-medium">{testimonial.company}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile Navigation Dots */}
            <div className="flex justify-center gap-2 mt-6">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentSlide(index);
                  }}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    currentSlide === index 
                      ? 'bg-[#c5a059] w-6' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  data-testid={`carousel-dot-${index}`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            {/* Mobile Arrow Navigation */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevSlide();
              }}
              className="absolute left-0 top-1/3 -translate-y-1/2 -translate-x-2 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-[#c5a059] hover:text-white transition-colors z-10"
              data-testid="carousel-prev"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextSlide();
              }}
              className="absolute right-0 top-1/3 -translate-y-1/2 translate-x-2 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-[#c5a059] hover:text-white transition-colors z-10"
              data-testid="carousel-next"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {activeVideo && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={closeModal}
          data-testid="video-modal"
        >
          <div 
            className="relative w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute -top-12 right-0 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
              data-testid="close-video-modal"
              aria-label="Close video"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            {/* Video Player */}
            <div className="relative aspect-video bg-black rounded-lg overflow-hidden shadow-2xl">
              <video
                ref={videoRef}
                src={activeVideo.videoUrl}
                controls
                autoPlay
                className="w-full h-full object-contain"
                playsInline
                poster={activeVideo.thumbnailUrl}
              >
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Video Caption */}
            <div className="mt-4 text-center">
              <p className="text-white text-lg font-medium">
                "{activeVideo.caption}"
              </p>
              <div className="mt-3 space-y-1">
                <p className="text-white font-semibold">{activeVideo.client}</p>
                <p className="text-gray-400 text-sm">{activeVideo.designation}</p>
                <div className="flex items-center justify-center gap-2 mt-1">
                  <Building2 className="w-4 h-4 text-[#c5a059]" />
                  <span className="text-gray-300 text-sm">{activeVideo.company}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export { ClientSuccessStories };
